import React from 'react'
import Menu from 'antd/lib/menu'
import Popover from 'antd/lib/popover'
import Row from 'antd/lib/row'
import Col from 'antd/lib/col'
import Checkbox from 'antd/lib/checkbox'
import styled from 'styled-components'
import { IconPlus as Icon, IconMute } from '@edulastic/icons'
import {
  greenPrimary,
  themeColor,
  accessibilityColors,
  mobileWidth,
} from '@edulastic/colors'
import './index.scss'
import { LANGUAGES_OPTIONS } from '@edulastic/constants/const/languages'
import { EduButton } from '@edulastic/common'
import { BetaTag } from '../BetaTag'

const BaseLanguageSelectorTab = ({
  handleSelectedLang,
  selectedLanguages,
  handleSaveLanguage,
  isFullWidth,
  isOffset,
  isBeta,
  popoverVisible,
  setPopoverVisible,
  handleChangeLanguage,
  currentLang,
  languageTabsToShow,
  showAddLanguageButton,
  isDisabled,
}) => {
  const menu = (
    <div style={{ width: 300 }}>
      <p>Select at least 1 and maximum 3</p>
      <Row gutter={[20, 20]}>
        {LANGUAGES_OPTIONS.map((language, index) => (
          <Col span={12}>
            <StyledCheckbox
              onChange={({ target }) =>
                handleSelectedLang(target.checked, language.value)
              }
              checked={selectedLanguages.includes(language.value)}
              disabled={
                (selectedLanguages.length === 3 &&
                  !selectedLanguages.includes(language.value)) ||
                isDisabled
              }
              data-cy={`language-checkbox-${(
                language?.label || ''
              ).toLowerCase()}`}
              tabIndex="0"
              id={`${index}-language-checkbox`}
            >
              {language.label}
              {language.isTTSDisabled && <IconMute />}
            </StyledCheckbox>
          </Col>
        ))}
      </Row>
      <EduButton
        block
        disabled={selectedLanguages.length === 0 || isDisabled}
        style={{ marginLeft: 0 }}
        onClick={handleSaveLanguage}
        data-cy="save-language-button"
      >
        Save
      </EduButton>
    </div>
  )

  const filteredLanguageTabs = LANGUAGES_OPTIONS.filter((lo) =>
    languageTabsToShow.includes(lo.value)
  )

  const onArrowNavigation = (direction, currentSelectedIndex) => {
    let requestedIndex = currentSelectedIndex
    if (direction === 'left') {
      requestedIndex = currentSelectedIndex - 1
    } else {
      requestedIndex = currentSelectedIndex + 1
    }
    if (requestedIndex < 0) {
      requestedIndex = filteredLanguageTabs.length - 1
    }
    if (requestedIndex >= filteredLanguageTabs.length) {
      requestedIndex = 0
    }
    const requestedId = `${filteredLanguageTabs[requestedIndex].value}-tab-control`
    document.getElementById(requestedId).focus()
  }

  const handleKeyPress = (event, currentSelectedIndex) => {
    if (event.key === 'Enter' || event.key === ' ') {
      handleChangeLanguage({
        key: filteredLanguageTabs[currentSelectedIndex].value,
      })
      return
    }
    if (event.key === 'ArrowLeft') {
      onArrowNavigation('left', currentSelectedIndex)
    }
    if (event.key === 'ArrowRight') {
      onArrowNavigation('right', currentSelectedIndex)
    }
  }

  return (
    <StyledRow type="flex" isFullWidth={isFullWidth} isOffset={isOffset}>
      {/* Student won't see language selector menu */}
      <Menu
        data-cy="language-selector"
        onClick={handleChangeLanguage}
        selectedKeys={[currentLang]}
        mode="horizontal"
        role="tablist"
      >
        {LANGUAGES_OPTIONS.filter((lo) =>
          languageTabsToShow.includes(lo.value)
        ).map((language, index) => (
          <StyledMenuItem
            tabIndex={currentLang === language.value ? '0' : '-1'}
            key={language.value}
            onKeyDown={(e) => handleKeyPress(e, index)}
            id={`${language.value}-tab-control`}
            role="tab"
            aria-selected={currentLang === language.value}
            aria-controls="language-selector-menu-tabpanel"
          >
            {language.label}
          </StyledMenuItem>
        ))}
      </Menu>
      {showAddLanguageButton && !isDisabled && (
        <Popover
          overlayClassName="language-selector-popover"
          content={menu}
          placement="bottomLeft"
          visible={popoverVisible}
          trigger="click"
          onVisibleChange={(visible) => setPopoverVisible(visible)}
        >
          <StyledButtonRow
            type="flex"
            align="middle"
            data-cy="add-language-button"
            tabIndex="0"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPopoverVisible(true)
                setTimeout(() => {
                  document.getElementById('0-language-checkbox').focus()
                }, 0)
              }
            }}
          >
            <IconPlus />
            <p>
              Add Language
              {isBeta && <StyledBetaTag>BETA</StyledBetaTag>}
            </p>
          </StyledButtonRow>
        </Popover>
      )}
    </StyledRow>
  )
}

export default BaseLanguageSelectorTab

export const StyledButtonRow = styled(Row)`
  cursor: pointer;
  &:hover {
    p {
      color: ${greenPrimary};
    }
    svg {
      fill: ${greenPrimary};
    }
  }
`

const StyledRow = styled(Row)`
  position: relative;
  top: ${({ isFullWidth }) => (isFullWidth ? '0' : '35px')};
  left: ${({ isFullWidth, isOffset }) =>
    isFullWidth ? (isOffset ? '315px' : '0px') : isOffset ? '315px' : '40px'};
  width: ${({ isFullWidth }) => (isFullWidth ? '100%' : '96%')};
  background: transparent;
  border-bottom: 1px solid #e8e8e8;

  @media (max-width: ${mobileWidth}) {
    left: 16px;
    top: ${({ isFullWidth }) => (isFullWidth ? '0' : '40px')};
  }

  .ant-menu-horizontal {
    border-bottom: 0;
  }

  .ant-menu-item {
    z-index: 1;

    &:hover,
    &.ant-menu-item-selected,
    &.ant-menu-item-active {
      color: ${themeColor};
      border-bottom: 2px solid ${themeColor};
    }
  }

  p {
    color: #999999;
    margin-left: 5px;
    margin-bottom: 0;
    font-weight: 600;
  }
`
export const IconPlus = styled(Icon)`
  width: 10px;
  height: 10px;
  fill: #999999;
`
const StyledBetaTag = styled(BetaTag)`
  position: relative;
  left: 8px;
  top: -8px;
`

const StyledMenuItem = styled(Menu.Item)`
  [tabindex='-1']:focus,
  &:focus {
    outline: 2px solid ${accessibilityColors.theme.blueHover} !important;
  }
`

const StyledCheckbox = styled(Checkbox)`
  &:focus {
    outline: 2px solid ${accessibilityColors.theme.blueHover} !important;
  }
`
